import { Core } from "@faire/design-tokens";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Grid } from "@faire/web/slate/Layout";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { ILeanProductTile } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { ProductTile } from "@faire/retailer/features/MarketplaceItems/ProductTile";
import { ProductPagePaths } from "@faire/retailer/paths/ProductPage";
import { UserStore } from "@faire/retailer/stores/domain/UserStore";
import { generateProductUrlForBrandPage } from "@faire/retailer/views/brand/utils/generateProductUrlForBrandPage";

interface IProps {
  products: IProduct[];
  trackProductClickEvent: (token: string) => void;
  solidTile?: boolean;
}

const ProductGridBase: React.FC<IProps> = (props) => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <StyledGrid
      columns={useResponsiveValue({
        mobileAndAbove: 2,
        tabletAndAbove: 3,
        desktopAndAbove: 5,
      })}
      columnTemplate={(column) => `repeat(${column}, minmax(0, 1fr))`}
      gap={12}
    >
      {props.products.map((product) => {
        if ((product.options || []).length === 0) {
          return null;
        }

        const isBrand = UserStore.get().isBrandUser;

        return (
          <StyledProductTile
            onProductClick={props.trackProductClickEvent}
            key={product.token}
            productTile={ILeanProductTile.from(product, strictLocalize)}
            generateProductLinkProps={(productToken: string) => ({
              productUrl: isBrand
                ? generateProductUrlForBrandPage(productToken)
                : ProductPagePaths.index(productToken),
            })}
            imageDimension={{
              mobileAndAbove: 150,
              tabletAndAbove: 350,
            }}
            newTab
            hideWholesalePrice={isBrand}
            hideQuickAddButton={isBrand}
            solid={!!props.solidTile}
          />
        );
      })}
    </StyledGrid>
  );
};

export const ProductGrid = observer(ProductGridBase);

const StyledGrid = styled(Grid)`
  width: 100%;
`;

const StyledProductTile = styled(ProductTile)<{ solid: boolean }>`
  background-color: ${(props) => (props.solid ? Core.icon.inverse : undefined)};
`;
